<template>
  <div
    class="cs-row"
    :class="'cs-row--' + message.direction"
    data-testid="chat-message"
    :data-msg-id="message.msgId"
    :data-direction="message.direction"
    :data-type="kind">
    <div class="cs-bubble" :class="['cs-bubble--' + message.direction, { 'cs-bubble--media': isMedia, 'cs-bubble--tpl': kind === 'template' && resolved }]">

      <!-- text -->
      <div v-if="kind === 'text'" class="cs-text" data-testid="chat-text"><span v-html="formatted"></span><span class="cs-time-pad"></span></div>

      <!-- image -->
      <template v-else-if="kind === 'image'">
        <img v-if="!mediaFailed" class="cs-image cs-image--zoom" data-testid="chat-image" :src="payload.image.link" alt="image" @click="openImage" @error="mediaFailed = true">
        <div v-else class="cs-text cs-unsupported" data-testid="chat-media-error"><span>[image could not be loaded]</span><span class="cs-time-pad"></span></div>
      </template>

      <!-- audio / voice note -->
      <template v-else-if="kind === 'audio'">
        <div v-if="!mediaFailed" class="cs-audio-wrap" data-testid="chat-audio">
          <audio ref="audio" :src="payload.audio.link" preload="metadata"
            @loadedmetadata="onAudioMeta" @durationchange="onAudioMeta" @timeupdate="audioCurrent = $event.target.currentTime"
            @play="audioPlaying = true" @pause="audioPlaying = false" @ended="onAudioEnded" @error="mediaFailed = true"></audio>
          <button type="button" class="cs-audio-rate" data-testid="chat-audio-rate" @click="cycleRate">{{ audioRate }}×</button>
          <button type="button" class="cs-audio-play" data-testid="chat-audio-play" :aria-label="audioPlaying ? 'Pause' : 'Play'" @click="toggleAudio">
            <i class="fa" :class="audioPlaying ? 'fa-pause' : 'fa-play'"></i>
          </button>
          <div class="cs-audio-main">
            <div class="cs-wave" data-testid="chat-audio-wave" @click="seekAudio">
              <span v-for="(h, i) in waveBars" :key="i" class="cs-wave-bar" :class="{ 'cs-wave-bar--played': i / waveBars.length <= audioProgress }" :style="{ height: h + 'px' }"></span>
              <span class="cs-wave-dot" :style="{ left: (audioProgress * 100) + '%' }"></span>
            </div>
            <span class="cs-audio-time" data-testid="chat-audio-time">{{ audioClock }}</span>
          </div>
        </div>
        <div v-else class="cs-text cs-unsupported" data-testid="chat-media-error"><span>[audio could not be loaded]</span><span class="cs-time-pad"></span></div>
      </template>

      <!-- uploaded video file -->
      <template v-else-if="kind === 'video_file'">
        <video v-if="!mediaFailed" class="cs-video" data-testid="chat-video" :src="payload.video.link" controls preload="metadata" @error="mediaFailed = true"></video>
        <div v-else class="cs-text cs-unsupported" data-testid="chat-media-error"><span>[video could not be loaded]</span><span class="cs-time-pad"></span></div>
      </template>

      <!-- document -->
      <a v-else-if="kind === 'document'" class="cs-doc" data-testid="chat-document" :href="payload.document.link" :download="payload.document.filename" target="_blank" rel="noopener noreferrer">
        <i class="fa fa-file-o"></i>
        <span class="cs-doc-meta">
          <span class="cs-doc-name" data-testid="chat-document-name">{{ payload.document.filename }}</span>
          <span class="cs-doc-size">{{ docSize }}</span>
        </span>
      </a>

      <!-- video (text message whose body is a video url) -->
      <video v-else-if="kind === 'video'" class="cs-video" data-testid="chat-video" :src="payload.text.body" controls preload="metadata"></video>

      <!-- interactive bodies -->
      <div v-else-if="isInteractiveBody" class="cs-text" data-testid="chat-text"><span v-html="format(payload.interactive.body.text)"></span><span class="cs-time-pad"></span></div>

      <!-- user interactive reply -->
      <div v-else-if="kind === 'reply'" class="cs-text" data-testid="chat-text"><span>{{ replyTitle }}</span><span class="cs-time-pad"></span></div>

      <!-- user location -->
      <div v-else-if="kind === 'location'" class="cs-text" data-testid="chat-text"><span>📍 {{ payload.location.latitude }}, {{ payload.location.longitude }}</span><span class="cs-time-pad"></span></div>

      <!-- template (resolved: header + body + footer + buttons) -->
      <div v-else-if="kind === 'template' && resolved" class="cs-tpl" data-testid="chat-template">
        <img v-if="resolved.header && resolved.header.type === 'image'" class="cs-image cs-tpl-header" data-testid="chat-image" :src="resolved.header.link" alt="header">
        <div v-else-if="resolved.header && resolved.header.type === 'text'" class="cs-text cs-tpl-title" data-testid="chat-template-header">{{ resolved.header.text }}</div>
        <div class="cs-text cs-tpl-body" data-testid="chat-text"><span v-html="format(resolved.body)"></span><span class="cs-time-pad"></span></div>
        <div v-if="resolved.footer" class="cs-tpl-footer" data-testid="chat-template-footer">{{ resolved.footer }}</div>
      </div>

      <!-- template (not resolved) -->
      <div v-else-if="kind === 'template'" class="cs-text cs-unsupported" data-testid="chat-template"><span><i class="fa fa-file-text-o"></i> [Template: {{ payload.template.name }}]</span><span class="cs-time-pad"></span></div>

      <!-- anything else -->
      <div v-else class="cs-text cs-unsupported" data-testid="chat-unsupported"><span>[unsupported: {{ payload.type }}]</span><span class="cs-time-pad"></span></div>

      <div v-if="caption" class="cs-text cs-caption" data-testid="chat-caption"><span v-html="format(caption)"></span><span class="cs-time-pad"></span></div>

      <span class="cs-time" :class="{ 'cs-time--media': isMedia && !caption }" data-testid="chat-time">{{ time }}<span v-if="statusMark" class="cs-status" :data-status="message.status" data-testid="chat-status"> {{ statusMark }}</span></span>

      <!-- action rows attached to the bubble -->
      <div v-if="kind === 'buttons'" class="cs-actions" data-testid="chat-buttons">
        <button
          v-for="b in payload.interactive.action.buttons"
          :key="b.reply.id"
          type="button"
          class="cs-action"
          :data-testid="'chat-button-' + b.reply.id"
          :data-button-id="b.reply.id"
          :disabled="disabled"
          @click="$emit('reply', { id: b.reply.id, title: b.reply.title, type: 'button_reply', sourceMsgId: message.msgId })">
          <i class="fa fa-reply"></i> {{ b.reply.title }}
        </button>
      </div>

      <div v-if="kind === 'list'" class="cs-actions">
        <button
          type="button"
          class="cs-action"
          data-testid="chat-list-open"
          :disabled="disabled"
          @click="openList">
          <i class="fa fa-list-ul"></i> {{ payload.interactive.action.button || 'Options' }}
        </button>
      </div>

      <div v-if="kind === 'location_request'" class="cs-actions">
        <button
          type="button"
          class="cs-action"
          data-testid="chat-location-btn"
          :disabled="disabled"
          @click="$emit('location', { sourceMsgId: message.msgId })">
          <i class="fa fa-map-marker"></i> Share location
        </button>
      </div>

      <div v-if="kind === 'template' && resolved && resolved.buttons && resolved.buttons.length" class="cs-actions" data-testid="chat-buttons">
        <button
          v-for="(b, i) in resolved.buttons"
          :key="i"
          type="button"
          class="cs-action"
          :data-testid="'chat-button-' + i"
          :disabled="disabled"
          @click="b.type === 'URL' && b.url ? openUrl(b.url) : $emit('reply', { id: String(i + 1), title: b.text, type: 'button_reply', sourceMsgId: message.msgId })">
          <i class="fa" :class="b.type === 'URL' ? 'fa-external-link' : 'fa-reply'"></i> {{ b.text }}
        </button>
      </div>

      <div v-if="kind === 'flow'" class="cs-actions">
        <button type="button" class="cs-action" data-testid="chat-flow-btn" disabled
          title="WhatsApp Flows cannot run in the simulator">
          <i class="fa fa-external-link"></i> {{ payload.interactive.action.parameters.flow_cta || 'Open form' }}
        </button>
      </div>
    </div>

    <!-- image viewer (like WhatsApp Web) -->
    <div v-if="imageOpen" class="cs-viewer" data-testid="chat-image-viewer" @click.self="closeImage">
      <div class="cs-viewer-bar">
        <span class="cs-viewer-time">{{ time }}</span>
        <button type="button" class="cs-viewer-close" data-testid="chat-image-close" aria-label="Close" @click="closeImage"><i class="fa fa-times"></i></button>
      </div>
      <div class="cs-viewer-body" @click.self="closeImage">
        <img class="cs-viewer-img" :src="payload.image.link" alt="image">
      </div>
      <div v-if="caption" class="cs-viewer-caption" data-testid="chat-image-viewer-caption">{{ caption }}</div>
    </div>

    <!-- list selection dialog (like WhatsApp Web) -->
    <div v-if="listOpen" class="cs-modal" data-testid="chat-list-dialog" @click.self="closeList">
      <div class="cs-dialog" role="dialog" aria-modal="true" :aria-label="payload.interactive.action.button || 'Options'">
        <div class="cs-dialog-header">
          <button type="button" class="cs-dialog-close" data-testid="chat-list-close" title="Close" @click="closeList">
            <i class="fa fa-times"></i>
          </button>
          <span class="cs-dialog-title" data-testid="chat-list-title">{{ payload.interactive.action.button || 'Options' }}</span>
        </div>
        <div class="cs-dialog-body" data-testid="chat-list">
          <template v-for="(section, si) in listSections">
            <div v-if="section.title" :key="'t' + si" class="cs-dialog-section">{{ section.title }}</div>
            <label
              v-for="r in section.rows"
              :key="si + '-' + r.id"
              class="cs-dialog-row"
              :class="{ 'cs-dialog-row--selected': selectedRow && selectedRow.id === r.id }"
              :data-testid="'chat-list-row-' + r.id"
              :data-row-id="r.id">
              <span class="cs-dialog-text">
                <span class="cs-dialog-row-title">{{ r.title }}</span>
                <span v-if="r.description" class="cs-dialog-row-desc">{{ r.description }}</span>
              </span>
              <input type="radio" class="cs-dialog-radio" name="cs-list-choice" :value="r.id" :checked="selectedRow && selectedRow.id === r.id" @change="selectedRow = r">
              <span class="cs-dialog-dot"></span>
            </label>
          </template>
        </div>
        <div class="cs-dialog-footer">
          <button type="button" class="cs-dialog-send" data-testid="chat-list-send" :disabled="!selectedRow" @click="confirmList">Send</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Renders one chat message. `message` = { msgId, ts, direction, payload }
// where payload is a Turn.io (WhatsApp) message payload. See docs/chat-interface/02-api-contract.md
const VIDEO_URL = /^https?:\/\/\S+\.(mp4|webm|mov|3gp)(\?\S*)?$/i

function escapeHtml (s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// WhatsApp style markup on already escaped text
function formatText (raw) {
  if (raw === null || raw === undefined) return ''
  let s = escapeHtml(raw)
  s = s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
  s = s.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
  s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?]|$)/g, '$1<em>$2</em>')
  s = s.replace(/~([^~\n]+)~/g, '<del>$1</del>')
  return s.replace(/\n/g, '<br>')
}

export default {
  name: 'ChatMessageBubble',
  props: {
    message: { type: Object, required: true },
    disabled: { type: Boolean, default: false }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  data () {
    return { listOpen: false, selectedRow: null, mediaFailed: false, imageOpen: false, audioPlaying: false, audioCurrent: 0, audioDuration: 0, audioRate: 1 }
  },
  computed: {
    payload () {
      return this.message.payload || {}
    },
    kind () {
      const p = this.payload
      if (p.type === 'text') {
        const body = p.text && p.text.body
        if (this.message.direction === 'out' && p.preview_url && VIDEO_URL.test((body || '').trim())) return 'video'
        return 'text'
      }
      if (p.type === 'image') return 'image'
      if (p.type === 'audio' && p.audio) return 'audio'
      if (p.type === 'video' && p.video) return 'video_file'
      if (p.type === 'document' && p.document) return 'document'
      if (p.type === 'template') return 'template'
      if (p.type === 'location') return 'location'
      if (p.type === 'interactive' && p.interactive) {
        const t = p.interactive.type
        if (t === 'button') return 'buttons'
        if (t === 'list') return 'list'
        if (t === 'location_request_message') return 'location_request'
        if (t === 'flow') return 'flow'
        if (t === 'button_reply' || t === 'list_reply') return 'reply'
      }
      return 'unsupported'
    },
    statusMark () {
      if (this.message.direction !== 'in') return ''
      return { sending: '…', sent: '✓', failed: '!' }[this.message.status] || ''
    },
    isMedia () {
      return ['image', 'video', 'video_file'].indexOf(this.kind) !== -1
    },
    resolved () {
      // UI-only for now: the chat service will attach the template text from Turn (getTurnTemplates)
      return this.message.resolvedTemplate || null
    },
    caption () {
      const p = this.payload
      const c = (p.image && p.image.caption) || (p.video && p.video.caption) || (p.document && p.document.caption)
      return c || ''
    },
    docSize () {
      const n = this.payload.document && this.payload.document.size
      if (!n) return ''
      return n > 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB'
    },
    isInteractiveBody () {
      return ['buttons', 'list', 'location_request', 'flow'].indexOf(this.kind) !== -1
    },
    formatted () {
      return formatText(this.payload.text && this.payload.text.body)
    },
    replyTitle () {
      const i = this.payload.interactive || {}
      const r = i.button_reply || i.list_reply || {}
      return r.title
    },
    listSections () {
      return (this.payload.interactive.action.sections || []).map(s => ({ title: s.title || '', rows: s.rows || [] }))
    },
    audioProgress () {
      return this.audioDuration > 0 ? Math.min(1, this.audioCurrent / this.audioDuration) : 0
    },
    audioClock () {
      // WhatsApp shows the elapsed time while playing, the total length otherwise
      const s = this.audioPlaying || this.audioCurrent > 0 ? this.audioCurrent : this.audioDuration
      const n = Math.floor(s || 0)
      return Math.floor(n / 60) + ':' + String(n % 60).padStart(2, '0')
    },
    waveBars () {
      // same bars for the same file (seeded from the link), the real waveform is not decoded
      let seed = 0
      const src = (this.payload.audio && this.payload.audio.link) || ''
      for (let i = 0; i < src.length; i++) seed = (seed * 31 + src.charCodeAt(i)) >>> 0
      const bars = []
      for (let i = 0; i < 40; i++) {
        seed = (seed * 1664525 + 1013904223) >>> 0
        bars.push(3 + Math.round((seed / 4294967296) * 17))
      }
      return bars
    },
    time () {
      const d = new Date(this.message.ts)
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return hh + ':' + mm
    }
  },
  methods: {
    format: formatText,
    onAudioMeta (e) {
      const d = e.target.duration
      if (isFinite(d)) this.audioDuration = d
    },
    onAudioEnded () {
      this.audioPlaying = false
      this.audioCurrent = 0
    },
    toggleAudio () {
      const a = this.$refs.audio
      if (!a) return
      if (a.paused) {
        a.playbackRate = this.audioRate
        a.play().catch(() => { this.mediaFailed = true })
      } else {
        a.pause()
      }
    },
    cycleRate () {
      this.audioRate = { 1: 1.5, 1.5: 2, 2: 1 }[this.audioRate] || 1
      if (this.$refs.audio) this.$refs.audio.playbackRate = this.audioRate
    },
    seekAudio (e) {
      const a = this.$refs.audio
      if (!a || !this.audioDuration) return
      const box = e.currentTarget.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width))
      a.currentTime = ratio * this.audioDuration
      this.audioCurrent = a.currentTime
    },
    openUrl (url) {
      window.open(url, '_blank', 'noopener')
    },
    openList () {
      this.selectedRow = null
      this.listOpen = true
      document.addEventListener('keydown', this.onKeydown)
    },
    closeList () {
      this.listOpen = false
      document.removeEventListener('keydown', this.onKeydown)
    },
    onKeydown (e) {
      if (e.key !== 'Escape') return
      if (this.imageOpen) this.closeImage()
      else this.closeList()
    },
    openImage () {
      this.imageOpen = true
      document.addEventListener('keydown', this.onKeydown)
    },
    closeImage () {
      this.imageOpen = false
      document.removeEventListener('keydown', this.onKeydown)
    },
    confirmList () {
      const r = this.selectedRow
      if (!r) return
      this.closeList()
      this.$emit('reply', { id: r.id, title: r.title, type: 'list_reply', sourceMsgId: this.message.msgId })
    }
  }
}
</script>

<style scoped>
.cs-row { display: flex; margin: 9px 0; }
.cs-row--out { justify-content: flex-start; }
.cs-row--in { justify-content: flex-end; }
.cs-bubble { position: relative; max-width: min(78%, 560px); min-width: 80px; padding: 7px 9px 6px 10px; border-radius: 8px; box-shadow: 0 1px 0.5px rgba(11,20,26,.25); text-align: left; font-size: 14.2px; line-height: 19px; color: var(--cs-text); word-wrap: break-word; overflow-wrap: anywhere; }
.cs-bubble--out { background: var(--cs-out); border-top-left-radius: 0; }
.cs-bubble--in { background: var(--cs-in); border-top-right-radius: 0; }
.cs-bubble--media { padding: 3px; max-width: min(78%, 300px); }
.cs-text { white-space: normal; }
.cs-text >>> a { color: var(--cs-link); }
.cs-text >>> strong { font-weight: 600; }
.cs-time-pad { display: inline-block; width: 64px; height: 1px; }
.cs-time { position: absolute; right: 8px; bottom: 5px; font-size: 11px; line-height: 15px; color: var(--cs-time); }
.cs-time--media { background: rgba(0,0,0,.45); color: #fff; border-radius: 8px; padding: 0 6px; right: 8px; bottom: 8px; }
.cs-image, .cs-video { display: block; width: 100%; max-width: 100%; max-height: 240px; object-fit: cover; border-radius: 6px; background: #000; }
.cs-image--zoom { cursor: zoom-in; }
.cs-viewer { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 1200; display: flex; flex-direction: column; background: rgba(11, 20, 26, .94); }
.cs-viewer-bar { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; color: #e9edef; }
.cs-viewer-time { font-size: 14px; color: #8696a0; }
.cs-viewer-close { width: 40px; height: 40px; border: 0; border-radius: 50%; background: transparent; color: #e9edef; font-size: 22px; cursor: pointer; }
.cs-viewer-close:hover { background: rgba(255,255,255,.1); }
.cs-viewer-body { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; padding: 0 24px; }
.cs-viewer-img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 4px; }
.cs-viewer-caption { padding: 12px 24px 20px; text-align: center; color: #e9edef; font-size: 15px; }
.cs-unsupported { color: var(--cs-time); font-style: italic; }
.cs-actions { margin: 8px -9px -6px -10px; border-top: 1px solid var(--cs-divider); }
.cs-bubble--media .cs-actions { margin: 6px -3px -3px; }
.cs-action { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 11px 8px; background: transparent; border: 0; border-top: 1px solid var(--cs-divider); color: var(--cs-action); font-size: 14px; font-weight: 600; cursor: pointer; }
.cs-action:first-child { border-top: 0; }
.cs-action:hover:not(:disabled) { background: var(--cs-hover); }
.cs-action:disabled { opacity: .5; cursor: not-allowed; }
.cs-status[data-status="failed"] { color: #ef5350; font-weight: 700; }
.cs-audio-wrap { display: flex; align-items: center; gap: 10px; min-width: 300px; padding: 4px 2px 16px; }
.cs-audio-rate { flex: none; min-width: 46px; height: 26px; padding: 0 10px; border: 0; border-radius: 13px; background: rgba(0,0,0,.35); color: var(--cs-text); font-size: 13px; font-weight: 600; cursor: pointer; }
.cs-audio-play { flex: none; width: 30px; height: 30px; padding: 0; border: 0; background: transparent; color: var(--cs-text); font-size: 20px; cursor: pointer; }
.cs-audio-main { flex: 1; min-width: 0; }
.cs-wave { position: relative; display: flex; align-items: center; justify-content: space-between; height: 24px; cursor: pointer; }
.cs-wave-bar { flex: none; width: 3px; border-radius: 2px; background: var(--cs-time); opacity: .55; }
.cs-wave-bar--played { background: var(--cs-text); opacity: .9; }
.cs-wave-dot { position: absolute; top: 50%; width: 13px; height: 13px; margin: -6px 0 0 -6px; border-radius: 50%; background: var(--cs-link); pointer-events: none; }
.cs-audio-time { position: absolute; bottom: 5px; font-size: 11px; line-height: 15px; color: var(--cs-time); }
.cs-caption { padding: 4px 6px 2px; }
.cs-doc { display: flex; align-items: center; gap: 10px; padding: 6px 4px; min-width: 200px; color: inherit; text-decoration: none; }
.cs-doc .fa { font-size: 28px; color: var(--cs-time); }
.cs-doc-meta { display: flex; flex-direction: column; min-width: 0; }
.cs-doc-name { font-size: 14px; word-break: break-all; }
.cs-doc-size { font-size: 12px; color: var(--cs-time); }
.cs-bubble--tpl { padding: 3px 3px 6px; }
.cs-tpl-header { margin-bottom: 4px; }
.cs-tpl-title { font-weight: 600; padding: 4px 6px 0; }
.cs-tpl-body { padding: 4px 6px 0; }
.cs-tpl-footer { padding: 2px 6px 0; font-size: 13px; color: var(--cs-time); }
/* list dialog */
.cs-modal { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(11, 20, 26, .65); }
.cs-dialog { width: 100%; max-width: 420px; max-height: 80vh; display: flex; flex-direction: column; background: var(--cs-panel); color: var(--cs-text); border-radius: 12px; box-shadow: 0 10px 40px rgba(0, 0, 0, .45); overflow: hidden; }
.cs-dialog-header { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid var(--cs-divider); }
.cs-dialog-close { width: 32px; height: 32px; border: 0; border-radius: 50%; background: transparent; color: var(--cs-time); font-size: 18px; cursor: pointer; }
.cs-dialog-close:hover { background: var(--cs-hover); color: var(--cs-text); }
.cs-dialog-title { font-size: 16px; font-weight: 500; }
.cs-dialog-body { flex: 1; overflow-y: auto; padding: 4px 0; }
.cs-dialog-section { padding: 10px 18px 4px; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: var(--cs-action); }
.cs-dialog-row { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 0; padding: 12px 18px; font-weight: normal; cursor: pointer; }
.cs-dialog-row:hover { background: var(--cs-hover); }
.cs-dialog-row--selected { background: var(--cs-hover); }
.cs-dialog-text { display: flex; flex-direction: column; min-width: 0; }
.cs-dialog-row-title { font-size: 15px; overflow-wrap: anywhere; }
.cs-dialog-row-desc { font-size: 13px; color: var(--cs-time); overflow-wrap: anywhere; }
.cs-dialog-radio { position: absolute; opacity: 0; pointer-events: none; }
.cs-dialog-dot { flex: none; width: 20px; height: 20px; border: 2px solid var(--cs-time); border-radius: 50%; display: inline-block; position: relative; }
.cs-dialog-row--selected .cs-dialog-dot { border-color: var(--cs-action); }
.cs-dialog-row--selected .cs-dialog-dot::after { content: ''; position: absolute; top: 3px; right: 3px; bottom: 3px; left: 3px; border-radius: 50%; background: var(--cs-action); }
.cs-dialog-footer { padding: 10px 14px; border-top: 1px solid var(--cs-divider); display: flex; justify-content: flex-end; }
.cs-dialog-send { min-width: 96px; padding: 9px 18px; border: 0; border-radius: 20px; background: #00a884; color: #fff; font-weight: 500; cursor: pointer; }
.cs-dialog-send:disabled { background: var(--cs-field); color: var(--cs-time); cursor: not-allowed; }
</style>



// WEBPACK FOOTER //
// MessageBubble.vue?6c1c98ac